import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';

import FeatherIcon from '../../components/common/FeatherIcon';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { copilotService } from '../../services/copilot/copilotService';
import './copilot-connection-gate.css';

const INTEGRATIONS_HREF = '/admin/integrations?category=saas_ai';

const COPILOT_PAGE_LABELS = [
  ['/copilot/security/incidents', 'Security Incidents'],
  ['/copilot/security/exceptions', 'Security Exceptions'],
  ['/copilot/security/signals', 'Security Signals'],
  ['/copilot/security/response', 'Security Response'],
  ['/copilot/security/skills', 'Security Skills'],
  ['/copilot/security/hunt', 'Security Hunt'],
  ['/copilot/intent-analysis', 'Intent Analysis'],
  ['/copilot/knowledge-gaps', 'Knowledge Gaps'],
  ['/copilot/recommendations', 'Recommendations'],
  ['/copilot/agent-network', 'Agent Network'],
  ['/copilot/audit-report', 'Audit Report'],
  ['/copilot/applications', 'Agent Registry'],
  ['/copilot/conversations', 'Conversations'],
  ['/copilot/interactions', 'Conversations'],
  ['/copilot/usage-roi', 'Usage & ROI'],
  ['/copilot/audit-log', 'Audit Log'],
  ['/copilot/compliance', 'Compliance'],
  ['/copilot/dashboard', 'Activation Dashboard'],
  ['/copilot/registry', 'Agent Registry'],
  ['/copilot/settings', 'Settings'],
  ['/copilot/posture', 'Access Management'],
  ['/copilot/sources', 'Source Intelligence'],
  ['/copilot/threats', 'Threats'],
  ['/copilot/alerts', 'Alerts'],
  ['/copilot/users', 'Users'],
];

export const getCopilotPageLabel = (pathname = '') => (
  COPILOT_PAGE_LABELS.find(([route]) => (
    pathname === route || pathname.startsWith(`${route}/`)
  ))?.[1] || 'Microsoft 365 Copilot'
);

export const IntegrationOnboardingEmptyState = ({
  pageLabel,
  productName,
  connectProductName = productName,
  paused = false,
  checking = false,
  onCheckAgain,
}) => (
  <main
    className="copilot-connection-page"
    aria-labelledby="copilot-connection-title"
    data-testid="copilot-connection-empty-state"
  >
    <div className="copilot-connection-card">
      <div className="copilot-connection-icon" aria-hidden="true">
        <FeatherIcon name={paused ? 'pause-circle' : 'link'} size={30} />
      </div>
      <p className="copilot-connection-eyebrow">{pageLabel}</p>
      <h1 id="copilot-connection-title">
        {paused
          ? `${productName} data collection is paused`
          : `Connect ${connectProductName} to see data`}
      </h1>
      <p className="copilot-connection-description">
        {paused
          ? `Resume the ${productName} integration before ${pageLabel} can receive new activity.`
          : `Set up the ${productName} integration before ${pageLabel} can show usage, security, and productivity insights.`}
      </p>

      <ol className="copilot-connection-steps" aria-label="Connection steps">
        <li>
          <span>1</span>
          <div><strong>{paused ? 'Open the integration' : 'Connect your tenant'}</strong><small>Use the guided {productName} setup.</small></div>
        </li>
        <li>
          <span>2</span>
          <div><strong>{paused ? 'Resume collection' : 'Verify credentials'}</strong><small>Confirm Saf3AI can read your configured Microsoft source.</small></div>
        </li>
        <li>
          <span>3</span>
          <div><strong>Return to this page</strong><small>Collected activity will replace this onboarding state.</small></div>
        </li>
      </ol>

      <div className="copilot-connection-actions">
        <Link className="copilot-connection-primary" to={INTEGRATIONS_HREF}>
          <FeatherIcon name="zap" size={15} aria-hidden="true" />
          {paused ? 'Manage integration' : `Connect ${productName}`}
        </Link>
        <button
          className="copilot-connection-secondary"
          type="button"
          onClick={onCheckAgain}
          disabled={checking}
        >
          <FeatherIcon name={checking ? 'loader' : 'refresh-cw'} size={15} aria-hidden="true" />
          {checking ? 'Checking…' : 'Check again'}
        </button>
      </div>
      <p className="copilot-connection-admin-note">
        Don’t manage integrations? Ask a Saf3AI administrator to connect {productName}.
      </p>
    </div>
  </main>
);

export const IntegrationConnectionGate = ({
  service,
  getPageLabel,
  productName,
  connectProductName = productName,
}) => {
  const { pathname } = useLocation();
  const mountedRef = useRef(false);
  const requestIdRef = useRef(0);
  const [connectionStatus, setConnectionStatus] = useState(undefined);
  const [checking, setChecking] = useState(true);

  const checkConnection = useCallback(async () => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setChecking(true);
    try {
      const status = await service.getConnectionStatus();
      if (mountedRef.current && requestId === requestIdRef.current) setConnectionStatus(status);
    } catch {
      // Connection-state lookup is an enhancement, not a reason to make the
      // product inaccessible. Fail open so page-level API handling still works.
      if (mountedRef.current && requestId === requestIdRef.current) setConnectionStatus(null);
    } finally {
      if (mountedRef.current && requestId === requestIdRef.current) setChecking(false);
    }
  }, [service]);

  useEffect(() => {
    mountedRef.current = true;
    checkConnection();
    return () => { mountedRef.current = false; };
  }, [checkConnection]);

  if (connectionStatus === undefined) {
    return <LoadingSpinner fullPage message={`Checking ${productName} connection…`} />;
  }

  const disconnected = connectionStatus?.connected === false;
  const paused = connectionStatus?.connected === true && connectionStatus?.enabled === false;
  if (disconnected || paused) {
    return (
      <IntegrationOnboardingEmptyState
        pageLabel={getPageLabel(pathname)}
        productName={productName}
        connectProductName={connectProductName}
        paused={paused}
        checking={checking}
        onCheckAgain={checkConnection}
      />
    );
  }

  return <Outlet />;
};

const CopilotConnectionGate = () => (
  <IntegrationConnectionGate
    service={copilotService}
    getPageLabel={getCopilotPageLabel}
    productName="Microsoft 365 Copilot"
    connectProductName="Microsoft Copilot"
  />
);

export const CopilotOnboardingEmptyState = (props) => (
  <IntegrationOnboardingEmptyState
    productName="Microsoft 365 Copilot"
    connectProductName="Microsoft Copilot"
    {...props}
  />
);

export default CopilotConnectionGate;
